import NextImage from 'next/image';

import logo from '../assets/logo.png';

import CTABackgroundMobile from '../assets/NearbyWatersBannerBackground-mobile.png';
import CTABackgroundTablet from '../assets/NearbyWatersBannerBackground-tablet.png';
import CTABackgroundDesktop from '../assets/NearbyWatersBannerBackground-desktop.png';
import ArrowBlack from '../assets/arrow-black.svg';
import { AppRatingLoader } from '../../../components/AppRatingSection/components/AppRatingLoader';
import { AppStoreButtons } from '../../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { Heading } from '../../../components/Heading/Heading';
import { Text } from '../../../components/Text/Text';
import { QRCode } from '../../../components/QRCode/QRCode';
import { RATING_ICON_WIDTH, RatingVariant } from '../../../components/Rating/constants/constants';
import { Rating } from '../../../components/Rating/Rating';
import {
  AVERAGE_APP_RATING,
  IOS_APP_RATING,
  ANDROID_RATING_COUNT,
  IOS_ALL_RATING_COUNT,
} from '../../../constants/constants';
import { useClientHasRendered } from '../../../helpers/hooks';
import { useIsAndroid } from '../../../helpers/hooks/useIsAndroid';
import { Source } from '../../../components/NextImage/Source';

interface IProps {
  sourceId?: string;
  title?: string;
  subTitle?: string;
}

export const NearbyWatersBanner = ({
  sourceId,
  title = 'Find fishing spots near you',
  subTitle = 'Use the Fishbrain app to discover the best fishing waters near you.',
}: IProps) => {
  const isAndroid = useIsAndroid();
  const clientHasRendered = useClientHasRendered();

  return (
    <div
      className={`relative mx-0 max-w-(--breakpoint-lg) rounded-xl p-6 sm:p-8 lg:mx-28 xl:mx-auto xl:w-full`}
    >
      <picture>
        <Source media="(min-width: 990px)" src={CTABackgroundDesktop}></Source>
        <Source media="(min-width: 600px)" src={CTABackgroundTablet}></Source>
        <NextImage
          data-testid="appBannerLogo"
          alt="Fishbrain"
          src={CTABackgroundMobile}
          fill
          className="rounded-xl"
        />
      </picture>
      <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="flex flex-row items-center sm:max-w-[50%] md:max-w-none md:gap-x-10">
          <NextImage
            data-testid="appBannerLogo"
            alt="Fishbrain"
            src={logo}
            unoptimized
            className="z-mid hidden max-h-24 justify-start md:flex"
          />
          <div className="md:max-w-auto z-mid text-sea-foam flex flex-col gap-2 text-center sm:text-left">
            <Heading as="div" className="text-trench" variant="xs">
              {title}
            </Heading>
            {subTitle && (
              <Text className="text-trench m-0" variant="sm">
                {subTitle}
              </Text>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-4">
          <div className={`z-mid text-trench`}>
            <Rating
              rating={isAndroid ? AVERAGE_APP_RATING : IOS_APP_RATING}
              variant={RatingVariant.allStars}
              textSize="sm"
              isLarge
              iconSize={RATING_ICON_WIDTH}
              reviewPointColor="text-trench"
            />
            {clientHasRendered ? (
              <Text variant="sm" as="span" className={`text-trench ml-1 font-semibold`}>
                {isAndroid
                  ? `${ANDROID_RATING_COUNT} app store ratings`
                  : `${IOS_ALL_RATING_COUNT} ratings`}
              </Text>
            ) : (
              <AppRatingLoader key="NearbyWatersBanner-apprating-key" />
            )}
          </div>

          <AppStoreButtons
            className="z-mid md:hidden"
            orientation="vertical"
            source="NearbyWatersBanner"
            sourceId={sourceId}
          />
        </div>

        <div className="z-mid relative ml-4 hidden items-center justify-center sm:justify-start md:flex">
          <div className="relative pb-2">
            <Text className={`text-trench m-0 -rotate-12 whitespace-pre-line`} variant="sm">
              {`Scan the QR code 
                to download the app!`}
            </Text>
            <NextImage
              alt="Arrow pointing to QR code"
              src={ArrowBlack}
              className="absolute right-0 bottom-0 translate-y-full"
              unoptimized
            />
          </div>

          <QRCode className="bg-sea-foam rounded-md p-2 md:ml-4" source="NearbyWatersBanner" />
        </div>
      </div>
    </div>
  );
};
